/**
 * Airtable Cache Module
 * 
 * Advanced in-memory caching system for Airtable API responses
 * Implements LRU (Least Recently Used) eviction policy, size limits,
 * statistics tracking, cache preloading, and debugging tools
 */

// Cache prefix to avoid key collisions
const CACHE_PREFIX = 'airtable:';

// Define the structure of cache entries
interface CacheEntry<T = any> {
  key: string;         // Full cache key
  data: T;             // Cached data
  timestamp: number;   // When the entry was created
  lastAccessed: number;// When the entry was last accessed
  size: number;        // Approximate size in bytes (JSON size)
}

// Cache statistics interface
interface CacheStats {
  hits: number;           // Cache hit count
  misses: number;         // Cache miss count
  evictions: number;      // Number of items evicted
  totalItems: number;     // Current number of items in cache
  totalSize: number;      // Approximate total size in bytes
  oldestItem: number;     // Timestamp of oldest item
  newestItem: number;     // Timestamp of newest item
  hitRatio: number;       // Hit ratio (hits / (hits + misses))
  averageItemAge: number; // Average age of items in seconds
}

// Advanced cache implementation with LRU tracking
class AdvancedCache {
  public cache: Map<string, CacheEntry>;
  public stats: CacheStats;
  public statsInterval: NodeJS.Timeout | null = null;
  public statsIntervalSeconds: number; // Store interval value in seconds
  public maxSize: number;
  public debug: boolean;
  public enabled: boolean;
  public preloadTables: string[] = [];
  public ttl: number;
  
  constructor({ statsInterval = 0, maxSize = 500, debug = false, enabled = true, preloadTables = [], ttl = 300 }: { statsInterval?: number, maxSize?: number, debug?: boolean, enabled?: boolean, preloadTables?: string[], ttl?: number } = {}) {
    this.cache = new Map();
    this.stats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      totalItems: 0,
      totalSize: 0,
      oldestItem: Date.now(),
      newestItem: 0,
      hitRatio: 0,
      averageItemAge: 0
    };
    this.maxSize = maxSize;
    this.debug = debug;
    this.enabled = enabled;
    this.preloadTables = preloadTables;
    this.ttl = ttl;
    this.statsIntervalSeconds = statsInterval; // Store the interval value
    
    // Start stats reporting if configured
    if (this.statsIntervalSeconds > 0) {
      this.startStatsReporting();
    }
  }
  
  /**
   * Get an item from the cache
   */
  get<T>(key: string): CacheEntry<T> | undefined {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    
    if (entry) {
      // Update last accessed time for LRU tracking
      entry.lastAccessed = Date.now();
      this.stats.hits++;
      this.logDebug(`Cache HIT: ${key}`);
    } else {
      this.stats.misses++;
      this.logDebug(`Cache MISS: ${key}`);
    }
    
    // Update hit ratio
    const total = this.stats.hits + this.stats.misses;
    this.stats.hitRatio = total > 0 ? this.stats.hits / total : 0;
    
    return entry;
  }
  
  /**
   * Store an item in the cache
   */
  set<T>(key: string, data: T): void {
    const now = Date.now();
    
    // Calculate approximate size of the data
    const jsonData = JSON.stringify(data);
    const size = jsonData.length * 2; // Rough estimate: 2 bytes per character
    
    const entry: CacheEntry<T> = {
      key,
      data,
      timestamp: now,
      lastAccessed: now,
      size
    };
    
    // Check if we need to evict items before adding new one
    this.enforceMaxSize(size);
    
    // Add the new entry
    this.cache.set(key, entry);
    
    // Update stats
    this.stats.totalItems = this.cache.size;
    this.stats.totalSize += size;
    this.stats.newestItem = Math.max(this.stats.newestItem, now);
    
    this.logDebug(`Cache SET: ${key} (${this.formatBytes(size)})`);
  }
  
  /**
   * Remove an item from the cache
   */
  delete(key: string): boolean {
    const entry = this.cache.get(key);
    if (entry) {
      this.stats.totalSize -= entry.size;
      this.cache.delete(key);
      this.stats.totalItems = this.cache.size;
      this.logDebug(`Cache DELETE: ${key}`);
      return true;
    }
    return false;
  }
  
  /**
   * Delete all cache entries that match a prefix
   */
  deleteByPrefix(prefix: string): number {
    let count = 0;
    
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        if (this.delete(key)) {
          count++;
        }
      }
    }
    
    this.logDebug(`Cache DELETE BY PREFIX: ${prefix} (${count} items)`);
    return count;
  }
  
  /**
   * Clear all items from the cache
   */
  clear(): void {
    this.cache.clear();
    this.resetStats();
    this.logDebug('Cache CLEARED');
  }
  
  /**
   * Check if the cache has an item
   */
  has(key: string): boolean {
    return this.cache.has(key);
  }
  
  /**
   * Get the keys in the cache
   */
  keys(): IterableIterator<string> {
    return this.cache.keys();
  }
  
  /**
   * Get the size of the cache
   */
  get size(): number {
    return this.cache.size;
  }
  
  /**
   * Log cache stats
   */
  getStats(): CacheStats {
    // Update average item age
    const now = Date.now();
    let totalAge = 0;
    let oldestTimestamp = now;
    
    for (const entry of this.cache.values()) {
      totalAge += now - entry.timestamp;
      oldestTimestamp = Math.min(oldestTimestamp, entry.timestamp);
    }
    
    this.stats.oldestItem = oldestTimestamp;
    this.stats.averageItemAge = this.cache.size > 0 ? totalAge / this.cache.size / 1000 : 0;
    
    return { ...this.stats };
  }
  
  /**
   * Reset statistics
   */
  private resetStats(): void {
    this.stats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      totalItems: 0,
      totalSize: 0,
      oldestItem: Date.now(),
      newestItem: 0,
      hitRatio: 0,
      averageItemAge: 0
    };
  }
  
  /**
   * Check if cache exceeds max size and evict items if needed
   */
  private enforceMaxSize(newItemSize: number): void {
    if (!this.maxSize || this.cache.size < this.maxSize) {
      return;
    }
    
    // Need to evict items using LRU policy
    if (this.enabled && this.enabled) {
      // Calculate how many items we need to evict to make room
      // We'll evict at least one, or more if needed to get under the limit
      const toEvict = Math.max(1, Math.ceil((this.cache.size + 1 - this.maxSize) / 10) * 10);
      
      // Sort entries by last accessed time (oldest first)
      const entries = Array.from(this.cache.entries())
        .map(([key, entry]) => ({ key, lastAccessed: entry.lastAccessed, size: entry.size }))
        .sort((a, b) => a.lastAccessed - b.lastAccessed);
      
      // Evict oldest items
      for (let i = 0; i < Math.min(toEvict, entries.length); i++) {
        const { key, size } = entries[i];
        this.cache.delete(key);
        this.stats.totalSize -= size;
        this.stats.evictions++;
        this.logDebug(`Cache EVICT (LRU): ${key}`);
      }
    } else {
      // Simple eviction - clear everything if we're at the limit
      if (this.cache.size >= this.maxSize) {
        this.logDebug(`Cache EVICT ALL (max size reached): ${this.cache.size} items`);
        this.stats.evictions += this.cache.size;
        this.clear();
      }
    }
    
    this.stats.totalItems = this.cache.size;
  }
  
  /**
   * Start periodic stats reporting
   */
  private startStatsReporting(): void {
    this.statsInterval = setInterval(() => {
      const stats = this.getStats();
      console.log('[Airtable Cache Stats]', {
        items: stats.totalItems,
        size: this.formatBytes(stats.totalSize),
        hitRatio: `${(stats.hitRatio * 100).toFixed(1)}%`,
        hits: stats.hits,
        misses: stats.misses,
        evictions: stats.evictions,
        avgAge: `${stats.averageItemAge.toFixed(1)}s`
      });
    }, this.statsIntervalSeconds * 1000); // Use the correct interval value
  }
  
  /**
   * Stop stats reporting
   */
  stopStatsReporting(): void {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
    }
  }
  
  /**
   * Format bytes to human-readable string
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
  
  /**
   * Debug logging
   */
  private logDebug(message: string): void {
    if (this.debug) {
      console.log(`[Cache Debug] ${message}`);
    }
  }
}

let cacheInstance: AdvancedCache | null = null;
export async function getCacheInstance(): Promise<AdvancedCache> {
  if (!cacheInstance) {
    const { airtableConfig } = await import('./config');
    cacheInstance = new AdvancedCache({
      statsInterval: airtableConfig.cache.statsInterval,
      maxSize: airtableConfig.cache.maxSize,
      debug: airtableConfig.cache.debug,
      enabled: airtableConfig.cache.enabled,
      preloadTables: airtableConfig.cache.preloadTables,
      ttl: airtableConfig.cache.ttl,
    });
  }
  return cacheInstance;
}

/**
 * Initialize cache
 */
export async function initializeCache(): Promise<boolean> {
  try {
    const cache = await getCacheInstance();
    if (!cache.enabled) {
      console.log('Airtable caching is disabled');
      return false;
    }
    
    console.log('Advanced in-memory cache initialized', {
      maxSize: cache.maxSize,
      lruEviction: cache.enabled ? 'enabled' : 'disabled',
      statsInterval: cache.statsInterval,
      preloadTables: cache.preloadTables.length
    });
    
    return true;
  } catch (error) {
    console.error('Failed to initialize cache:', error);
    return false;
  }
}

/**
 * Get cached data using a key
 */
export async function getCachedData<T>(key: string): Promise<T | null> {
  const cache = await getCacheInstance();
  if (!cache.enabled) {
    return null;
  }

  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    const cachedItem = cache.get<T>(cacheKey);
    
    if (!cachedItem) {
      return null;
    }
    
    // Check if cache has expired
    const now = Date.now();
    if (cachedItem && typeof cachedItem.timestamp === 'number' && cache && typeof cache.ttl === 'number' && cachedItem.timestamp !== null) {
      if ((now - (cachedItem.timestamp as number)) > (cache.ttl * 1000)) {
        cache.delete(cacheKey);
        return null;
      }
    }
    
    return cachedItem.data;
  } catch (error) {
    console.error('Error retrieving cached data:', error);
    return null;
  }
}

/**
 * Store data in cache with TTL
 */
export async function setCachedData(
  key: string, 
  data: any, 
  ttl?: number
): Promise<boolean> {
  const cache = await getCacheInstance();
  const effectiveTtl = ttl !== undefined ? ttl : cache.ttl;

  if (!cache.enabled) {
    return false;
  }

  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    cache.set(cacheKey, data);
    return true;
  } catch (error) {
    console.error('Error storing cached data:', error);
    return false;
  }
}

/**
 * Invalidate a specific cache entry
 */
export async function invalidateCache(key: string): Promise<boolean> {
  const cache = await getCacheInstance();
  if (!cache.enabled) {
    return false;
  }

  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    return cache.delete(cacheKey);
  } catch (error) {
    console.error('Error invalidating cache:', error);
    return false;
  }
}

/**
 * Invalidate all cache entries with a specific prefix
 */
export async function invalidateCacheByPrefix(prefix: string): Promise<boolean> {
  const cache = await getCacheInstance();
  if (!cache.enabled) {
    return false;
  }

  try {
    const fullPrefix = `${CACHE_PREFIX}${prefix}`;
    const count = cache.deleteByPrefix(fullPrefix);
    return count > 0;
  } catch (error) {
    console.error('Error invalidating cache by prefix:', error);
    return false;
  }
}

/**
 * Close cache connections (stops stats reporting)
 */
export async function closeCache(): Promise<void> {
  const cache = await getCacheInstance();
  if (cache) {
    cache.stopStatsReporting();
  }
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<CacheStats> {
  const cache = await getCacheInstance();
  return cache.getStats();
}

/**
 * Clear entire cache
 */
export async function clearCache(): Promise<boolean> {
  const cache = await getCacheInstance();
  if (!cache.enabled) {
    return false;
  }
  
  try {
    cache.clear();
    return true;
  } catch (error) {
    console.error('Error clearing cache:', error);
    return false;
  }
}

/**
 * Preload frequently accessed data into cache
 */
export async function preloadCache(
  fetchFn: (table: string) => Promise<any>
): Promise<{ success: boolean; loaded: number }> {
  const cache = await getCacheInstance();
  if (!cache.enabled || !cache.preloadTables.length) {
    return { success: false, loaded: 0 };
  }
  
  try {
    let loaded = 0;
    
    // Load each configured table
    for (const table of cache.preloadTables) {
      try {
        await fetchFn(table);
        loaded++;
      } catch (err) {
        console.error(`Failed to preload table ${table}:`, err);
      }
    }
    
    console.log(`Cache preloaded with ${loaded}/${cache.preloadTables.length} tables`);
    return { success: true, loaded };
  } catch (error) {
    console.error('Error preloading cache:', error);
    return { success: false, loaded: 0 };
  }
}

// (No top-level cache initialization) 
